/**
 * DbsDirtyGate — unsaved-progress guard for the Launch Portal's
 * DBS Application section.
 *
 * Problem it solves (owner directive, Feb-2026 — Sam):
 *   Once a franchisee starts entering DBS fields, they must NOT be
 *   able to leave the section (or close the browser) and lose their
 *   progress silently. DBS is a long, legally sensitive form — a
 *   quiet drop-off is far more expensive than a confirm dialog.
 *
 * Scope:
 *   - Switching Launch Portal sections (inline nav + sticky-header
 *     Launch nav swap).
 *   - Switching Discovery sections (mobile strip + desktop sidebar).
 *   - Collapsing / expanding the Discovery heading.
 *   - Closing the browser tab / refreshing / navigating off-site —
 *     delegated to the browser's native beforeunload prompt (we only
 *     attach the listener while the form is dirty; the browser owns
 *     the wording).
 *
 * Scope (NOT guarded):
 *   - An untouched form (``isDirty === false``).
 *   - Any navigation that happens AFTER a successful submission —
 *     ``markClean()`` is called the instant the server returns 2xx.
 *   - A failed submission preserves the dirty flag so the user is
 *     still warned about leaving.
 *
 * Shape of the context value:
 *     {
 *       isDirty:      boolean,
 *       markDirty:    () => void,
 *       markClean:    () => void,
 *       requestLeave: (next?: () => void) => void,
 *     }
 *
 *   ``requestLeave`` is the one entry point every nav button goes
 *   through. If the form is clean it calls ``next()`` straight away;
 *   if dirty it defers ``next()`` behind the confirm dialog.
 *
 * ``useDbsDirtyGate()`` returns a NO-OP default when no provider is
 * mounted — so components can call ``requestLeave(fn)`` unconditionally
 * without having to null-check first.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DEFAULT_GATE = {
  isDirty: false,
  markDirty: () => {},
  markClean: () => {},
  requestLeave: (next) => { next && next(); },
};

const DbsDirtyContext = createContext(DEFAULT_GATE);

export const useDbsDirtyGate = () => useContext(DbsDirtyContext);

export function DbsDirtyGate({ children }) {
  // Mirror the dirty state in a ref so ``requestLeave`` can decide
  // synchronously inside an event handler without racing against the
  // next React render.
  const dirtyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingNext, setPendingNext] = useState(null);

  const markDirty = useCallback(() => {
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    setIsDirty(true);
  }, []);

  const markClean = useCallback(() => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setIsDirty(false);
  }, []);

  const requestLeave = useCallback((next) => {
    if (!dirtyRef.current) {
      next && next();
      return;
    }
    // Wrap the function so React doesn't call it when stored as state.
    setPendingNext(() => next || null);
  }, []);

  // Native beforeunload — browsers control the wording. We only
  // attach while dirty so the prompt doesn't fire gratuitously.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Browser Back / Forward guard (Feb-2026 — Sam). Without this the
  // in-app dialog never fires for a popstate because the gate only
  // intercepts click handlers it wraps directly. We push a sentinel
  // history entry while dirty; the first ``popstate`` pops the
  // sentinel, we re-push it immediately so the URL stays put, then
  // show the same confirm dialog. If the user picks Leave we clear
  // the dirty flag and ``go(-2)`` to pop both the re-pushed sentinel
  // and the real page entry so the browser lands on wherever Back
  // was originally pointing. If they pick Stay the sentinel remains
  // in place and the next Back repeats the prompt.
  useEffect(() => {
    if (!isDirty) return undefined;
    const SENTINEL_KEY = "__dbsDirtySentinel";
    window.history.pushState({ [SENTINEL_KEY]: true }, "");
    const onPop = () => {
      if (!dirtyRef.current) return;
      window.history.pushState({ [SENTINEL_KEY]: true }, "");
      setPendingNext(() => () => {
        // Dirty was cleared by confirmLeave before this runs; the
        // ``popstate`` handler re-mounted above will therefore bail
        // on the next event and let the real pop through.
        window.history.go(-2);
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isDirty]);

  const confirmLeave = () => {
    const next = pendingNext;
    setPendingNext(null);
    // The confirmed leave invariably drops the form state (the DBS
    // section unmounts), so clear dirty here to keep the gate honest
    // in the window between the setState above and the next render.
    dirtyRef.current = false;
    setIsDirty(false);
    next && next();
  };

  const cancelLeave = () => {
    setPendingNext(null);
  };

  return (
    <DbsDirtyContext.Provider value={{ isDirty, markDirty, markClean, requestLeave }}>
      {children}
      <AlertDialog
        open={!!pendingNext}
        onOpenChange={(open) => { if (!open) cancelLeave(); }}
      >
        <AlertDialogContent data-testid="dbs-dirty-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved progress</AlertDialogTitle>
            <AlertDialogDescription data-testid="dbs-dirty-leave-dialog-body">
              If you leave this page, all progress will be lost
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelLeave} data-testid="dbs-dirty-stay-btn">
              Stay on this page
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeave} data-testid="dbs-dirty-leave-btn">
              Leave page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DbsDirtyContext.Provider>
  );
}
